<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Информационные страницы</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте добавим и&nbsp;заполним информационные страницы для вашего сайта.</p>

            <p>В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Сайт&raquo;.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            На&nbsp;открывшейся странице выберите в&nbsp;меню слева раздел &quot;Раздел&quot;О компании&quot;&quot;.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-dobavit-i-redaktirovat"
            class="typography__headline2"
          >Как добавить и редактировать
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              На&nbsp;сайте будет доступен отдельный раздел со&nbsp;всеми статьями. Также ссылки на&nbsp;отдельные
              статьи можно копировать и&nbsp;использовать в&nbsp;других статьях, баннерах на&nbsp;главной сайта, для
              оформления подписей с&nbsp;cookie и&nbsp;политиками сайта.
            </p>

            <p>В&nbsp;&laquo;Общее&raquo; можно изменить название раздела.</p>

            <p>
              Под разделом &laquo;Общее&raquo; можно добавить новую информационную страницу по&nbsp;кнопке
              &laquo;Добавить страницу&raquo;.
            </p>

            <p>
              На&nbsp;этих страницах можно расположить любую информацию&nbsp;&mdash; историю компании,
              <BaseLink
                class="ui-link-secondary"
                to="/faq/articles/politiki-konfidencialnosti"
              >политики конфиденциальности или cookies
              </BaseLink>
              , условия акций и&nbsp;другое.
            </p>

            <p>После внесения изменений нажмите &laquo;Сохранить&raquo; внизу блока.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>После добавления новой страницы можно будет перейти к&nbsp;её&nbsp;настройке и&nbsp;редактированию.</p>

            <p>
              По-умолчанию она скрыта с&nbsp;сайта, чтобы можно было сначала её&nbsp;настроить и&nbsp;показать когда
              будет готова.
            </p>

            <p>Ниже нужно ввести название страницы и&nbsp;можно задать ей&nbsp;конечную ссылку для открытия.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            Теперь можно перейти к&nbsp;редактированию содержания страницы по&nbsp;кнопке &laquo;Редактировать
            страницу&raquo;.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>
              Справа откроется редактор, в&nbsp;котором можно ввести текст, добавить изображения и&nbsp;другие нужные
              элементы.
            </p>

            <p>После внесения изменений нажмите &laquo;Сохранить&raquo; над редактором.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-6.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>После сохранения, справа можно посмотреть превью получившейся страницы.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/informatsionnyye-stranitsy-7.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Сайт",
    url: "/faq/categories/sayt",
  },
  {
    name: "Информационные страницы",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Как&nbsp;добавить&nbsp;и&nbsp;редактировать",
    url: "kak-dobavit-i-redaktirovat",
  },
];

const relatedArticles = [
  {
    name: "Сайт для клиентов",
    slug: "sayt-dlya-klientov",
  },
  {
    name: "Конструктор сайта",
    slug: "konstruktor-sayta",
  },
  {
    name: "Политики конфиденциальности",
    slug: "politiki-konfidencialnosti",
  },
];

useSeoMeta({
  title: "Информационные страницы",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
